/**
 * What's New Modal Service
 *
 * Manages localStorage operations for tracking modal dismissal state
 * across different feature versions. Provides a complete solution for displaying feature
 * release information modals with dismissal persistence and error recovery.
 *
 * Key Features:
 * - localStorage-based dismissal tracking with version awareness
 * - Graceful error handling for private browsing mode and quota exceeded scenarios
 * - Version-aware modal display logic with safe defaults
 * - Comprehensive type safety and debug logging
 * - Caching optimization to avoid repeated lookups
 *
 * Integration Example:
 * In your React component, use shouldShowModalForVersion(version) with the version
 * from your feature config to determine modal visibility, and call
 * markModalAsDismissed(version) when the user dismisses the modal.
 *
 * For React Context integration:
 * Create a context provider that loads the feature config, uses the config.version
 * with shouldShowModalForVersion(config.version) as initial state, and calls
 * markModalAsDismissed(config.version) on user dismissal.
 *
 * IMPORTANT: Always use the version from your feature config (config.version) rather
 * than getCurrentFeatureVersion() to ensure consistency across the application.
 */

/**
 * Checks if localStorage is available and accessible
 * Handles cases where localStorage is null or inaccessible (private browsing, etc.)
 * @returns true if localStorage is available, false otherwise
 */
const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = "__whatsNewService_test__";
    localStorage.setItem(testKey, "test");
    localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    // localStorage is not available (private browsing, quota exceeded, etc.)
    return false;
  }
};

/**
 * Generates a localStorage key for a specific version's modal dismissal state
 * @param version - The feature version (e.g., "1.0.0", "2.1.0")
 * @returns The localStorage key for this version's dismissal state
 */
export const getModalDismissedKey = (version: string): string => {
  return `whats_new_modal_dismissed_v${version}`;
};

/**
 * Checks if a user has previously dismissed the "What's New" modal for a specific version
 * Gracefully handles localStorage access errors by returning false as fallback
 *
 * @param version - The feature version to check
 * @returns true if the modal has been dismissed by the user, false otherwise (or if storage unavailable)
 */
export const hasUserDismissedModal = (version: string): boolean => {
  try {
    if (!isLocalStorageAvailable()) {
      return false;
    }

    const key = getModalDismissedKey(version);
    return localStorage.getItem(key) === "true";
  } catch (error) {
    // Log error for debugging but don't throw
    if (error instanceof Error) {
      console.warn(
        `[whatsNewService] Error checking modal dismissal state: ${error.message}`
      );
    }
    // Return false as fallback - show the modal if we can't check storage
    return false;
  }
};

/**
 * Marks the "What's New" modal as dismissed for a specific version
 * This persists the dismissal state in localStorage
 * Gracefully handles storage errors without crashing
 *
 * @param version - The feature version being dismissed
 * @throws Does not throw - all errors are logged and handled gracefully
 */
export const markModalAsDismissed = (version: string): void => {
  try {
    if (!isLocalStorageAvailable()) {
      console.warn(
        "[whatsNewService] localStorage is not available, dismissal state will not be persisted"
      );
      return;
    }

    const key = getModalDismissedKey(version);
    localStorage.setItem(key, "true");
  } catch (error) {
    // Log error for debugging but don't throw
    if (error instanceof Error) {
      if (error.name === "QuotaExceededError") {
        console.warn(
          "[whatsNewService] localStorage quota exceeded, dismissal state could not be saved"
        );
      } else if (error.name === "SecurityError") {
        console.warn(
          "[whatsNewService] SecurityError accessing localStorage, dismissal state could not be saved"
        );
      } else {
        console.warn(
          `[whatsNewService] Error persisting modal dismissal state: ${error.message}`
        );
      }
    }
    // Gracefully continue - the modal will show again on next visit if we can't save
  }
};

/**
 * Cache for the current feature version to avoid repeated lookups
 */
let cachedFeatureVersion: string | null = null;

/**
 * Gets the current feature version from configuration
 * Sources the version from multiple locations in order of preference:
 * 1. window.APP_FEATURE_VERSION (runtime configuration)
 * 2. REACT_APP_FEATURE_VERSION (environment variable)
 * 3. Default fallback "1.0.0"
 *
 * The result is cached to avoid repeated lookups after the first call.
 *
 * @returns The current feature version string (defaults to "1.0.0" if not found)
 * @deprecated Prefer using the version from your loaded feature config (config.version)
 * to ensure consistency across the application. This function is kept for backward
 * compatibility and as a fallback mechanism only.
 */
export const getCurrentFeatureVersion = (): string => {
  // Return cached version if available
  if (cachedFeatureVersion) {
    return cachedFeatureVersion;
  }

  try {
    // Try to get version from window.APP_FEATURE_VERSION if set by the app
    if (typeof window !== "undefined" && (window as any).APP_FEATURE_VERSION) {
      const version: string = (window as any).APP_FEATURE_VERSION;
      cachedFeatureVersion = version;
      return version;
    }

    // Try to get version from environment variable (build-time)
    if (
      typeof process !== "undefined" &&
      process.env.REACT_APP_FEATURE_VERSION
    ) {
      const version: string = process.env.REACT_APP_FEATURE_VERSION;
      cachedFeatureVersion = version;
      return version;
    }

    // Default fallback version
    const defaultVersion = "1.0.0";
    cachedFeatureVersion = defaultVersion;
    return defaultVersion;
  } catch (error) {
    // Log error but don't throw - use safe default
    if (error instanceof Error) {
      console.warn(
        `[whatsNewService] Error retrieving feature version: ${error.message}`
      );
    }
    // Cache and return default
    cachedFeatureVersion = "1.0.0";
    return cachedFeatureVersion;
  }
};

/**
 * Clears the cached feature version
 * Useful for testing or when the version needs to be re-evaluated
 *
 * @internal This function is primarily for testing purposes
 */
export const clearVersionCache = (): void => {
  cachedFeatureVersion = null;
};

/**
 * Determines whether the "What's New" modal should be shown to the user
 * Combines current version check and dismissal state to make the final visibility decision
 *
 * The modal is shown only if:
 * 1. A valid feature version exists and is not empty
 * 2. The user has NOT previously dismissed the modal for this version
 *
 * Returns false (hide modal) for safety if:
 * - Version is undefined or empty
 * - User has dismissed the modal for this version
 * - Storage is unavailable or any error occurs
 *
 * @param version - Optional. The feature version to check. If not provided, uses getCurrentFeatureVersion()
 * @returns true if the modal should be shown, false otherwise (safe default)
 * @throws Does not throw - all errors are logged and return false safely
 * @deprecated Use shouldShowModalForVersion() with explicit version from config instead
 */
export const shouldShowModal = (version?: string): boolean => {
  try {
    const currentVersion = version || getCurrentFeatureVersion();

    // Null/undefined checks for safety
    if (
      !currentVersion ||
      typeof currentVersion !== "string" ||
      currentVersion.trim() === ""
    ) {
      console.warn(
        "[whatsNewService] Invalid or missing feature version, hiding modal for safety"
      );
      return false;
    }

    // Check if user has already dismissed this version's modal
    const userDismissed = hasUserDismissedModal(currentVersion);
    console.log(
      `[whatsNewService] shouldShowModal for version ${currentVersion}: userDismissed=${userDismissed}`
    );
    // Show modal only if user hasn't dismissed it for this version
    return !userDismissed;
  } catch (error) {
    // Log error but don't throw - default to false (hide modal) for safety
    if (error instanceof Error) {
      console.warn(
        `[whatsNewService] Error in shouldShowModal: ${error.message}`
      );
    }
    return false;
  }
};

/**
 * Determines whether the modal should be shown for a specific version
 * Helper function for cases where you want to check a specific version
 * instead of using the current feature version
 *
 * The modal is shown only if the user has NOT previously dismissed
 * the modal for the specified version.
 *
 * @param version - The feature version to check
 * @returns true if the modal should be shown for this version, false otherwise
 * @throws Does not throw - all errors are logged and return false safely
 */
export const shouldShowModalForVersion = (version: string): boolean => {
  try {
    // Null/undefined checks for safety
    if (!version || typeof version !== "string" || version.trim() === "") {
      console.warn(
        "[whatsNewService] Invalid version provided to shouldShowModalForVersion, returning false"
      );
      return false;
    }

    // Show modal only if user hasn't dismissed it for this version
    return !hasUserDismissedModal(version);
  } catch (error) {
    // Log error but don't throw - default to false (hide modal) for safety
    if (error instanceof Error) {
      console.warn(
        `[whatsNewService] Error in shouldShowModalForVersion: ${error.message}`
      );
    }
    return false;
  }
};

/**
 * Determines if a new version is available by comparing current version with last seen version
 * Uses semantic versioning (MAJOR.MINOR.PATCH) comparison for accurate version detection
 * Falls back to string comparison for malformed versions
 *
 * This is useful for detecting when the feature version has changed between user visits
 * and reset modal dismissal state accordingly.
 *
 * Returns true if:
 * - No last seen version exists (first time or cleared)
 * - Current version is numerically greater than last seen version
 *
 * Returns false if:
 * - Versions are identical
 * - Last seen version is greater than current version
 *
 * @param currentVersion - The current feature version (e.g., "1.1.0")
 * @param lastSeenVersion - The last version the user has seen (e.g., "1.0.0"), or null
 * @returns true if a new version is available, false otherwise
 * @throws Does not throw - returns false safely on any error
 */
export const isNewVersionAvailable = (
  currentVersion: string,
  lastSeenVersion: string | null
): boolean => {
  try {
    // If no last seen version, it's a new version (first time or cleared)
    if (!lastSeenVersion) {
      return true;
    }

    // Validate inputs
    if (!currentVersion || typeof currentVersion !== "string") {
      return false;
    }

    // Use semantic version comparison
    // Returns > 0 if current > last, meaning a new version is available
    const comparison = compareSemanticVersions(currentVersion, lastSeenVersion);
    return comparison > 0;
  } catch (error) {
    // Log error but don't throw - default to false for safety
    if (error instanceof Error) {
      console.warn(
        `[whatsNewService] Error in isNewVersionAvailable: ${error.message}`
      );
    }
    return false;
  }
};

/**
 * Compares two semantic versions and returns which is newer
 * Parses versions in MAJOR.MINOR.PATCH format and compares numerically
 * Falls back to string comparison for malformed versions
 *
 * Examples:
 * - compareSemanticVersions("1.0.0", "1.0.1") returns -1 (version1 < version2)
 * - compareSemanticVersions("2.0.0", "1.9.9") returns 1 (version1 > version2)
 * - compareSemanticVersions("1.0.0", "1.0.0") returns 0 (equal)
 *
 * @param version1 - First version string to compare
 * @param version2 - Second version string to compare
 * @returns -1 if version1 < version2, 0 if equal, 1 if version1 > version2
 * @throws Does not throw - returns 0 (equal) safely on any parsing error
 */
const compareSemanticVersions = (
  version1: string,
  version2: string
): number => {
  try {
    // Split versions by period to get major, minor, patch
    const parts1 = version1.split(".").map((part) => {
      const num = parseInt(part, 10);
      return isNaN(num) ? 0 : num;
    });
    const parts2 = version2.split(".").map((part) => {
      const num = parseInt(part, 10);
      return isNaN(num) ? 0 : num;
    });

    // Pad arrays to same length with zeros for missing parts
    const maxLength = Math.max(parts1.length, parts2.length);
    while (parts1.length < maxLength) parts1.push(0);
    while (parts2.length < maxLength) parts2.push(0);

    // Compare each part numerically
    for (let i = 0; i < maxLength; i++) {
      if (parts1[i] < parts2[i]) return -1;
      if (parts1[i] > parts2[i]) return 1;
    }

    // All parts are equal
    return 0;
  } catch (error) {
    // Log error but don't throw - default to equal for safety
    if (error instanceof Error) {
      console.warn(
        `[whatsNewService] Error in compareSemanticVersions: ${error.message}`
      );
    }
    // Fall back to string comparison as last resort
    try {
      if (version1 < version2) return -1;
      if (version1 > version2) return 1;
      return 0;
    } catch {
      return 0;
    }
  }
};

/**
 * Marks a version as seen by the user (without requiring dismissal)
 * This tracks when a user has been shown the modal, even if they haven't clicked "Got It"
 * Uses a single localStorage key to track the last seen version
 *
 * @param version - The feature version that was shown to the user
 * @throws Does not throw - all errors are logged and handled gracefully
 */
export const markVersionAsSeen = (version: string): void => {
  try {
    if (!isLocalStorageAvailable()) {
      console.warn(
        "[whatsNewService] localStorage is not available, seen version will not be persisted"
      );
      return;
    }

    localStorage.setItem("whats_new_last_seen_version", version);
  } catch (error) {
    // Log error for debugging but don't throw
    if (error instanceof Error) {
      if (error.name === "QuotaExceededError") {
        console.warn(
          "[whatsNewService] localStorage quota exceeded, seen version could not be saved"
        );
      } else if (error.name === "SecurityError") {
        console.warn(
          "[whatsNewService] SecurityError accessing localStorage, seen version could not be saved"
        );
      } else {
        console.warn(
          `[whatsNewService] Error persisting seen version: ${error.message}`
        );
      }
    }
  }
};

/**
 * Retrieves the most recent version that was previously seen by the user
 * Reads from a single localStorage key that tracks the last shown version
 * This is separate from dismissal tracking to handle page reloads before dismissal
 *
 * @returns The last seen version string (e.g., "1.1.0"), or null if no version found
 * @throws Does not throw - returns null safely on any error
 */
export const getLastSeenVersion = (): string | null => {
  try {
    if (!isLocalStorageAvailable()) {
      return null;
    }

    // Read the last seen version from the single localStorage key
    const lastSeen = localStorage.getItem("whats_new_last_seen_version");
    return lastSeen;
  } catch (error) {
    // Log error but don't throw - default to null for safety
    if (error instanceof Error) {
      console.warn(
        `[whatsNewService] Error in getLastSeenVersion: ${error.message}`
      );
    }
    return null;
  }
};
